import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'type' | 'children' | 'value'> {
  /** On or off. The switch holds no state of its own. */
  checked: boolean;
  /** Called with the state the person asked for — the opposite of `checked`. */
  onCheckedChange: (next: boolean) => void;
  /** What the switch turns on and off. Without one, pass an `aria-label`. */
  label?: ReactNode;
  /** One quiet line under the label, read out as the switch's description. */
  hint?: ReactNode;
  /** Keeps the label for screen readers only. */
  hideLabel?: boolean;
  containerClassName?: string;
}

/**
 * An on/off switch — a setting that takes effect the moment it is flipped
 * (owner, 24 Sep 2026: "Add a toggle for switching vehicle model off and on").
 *
 * A checkbox answers a question a form will send later; a switch *is* the
 * action, which is why this is its own control rather than a Checkbox skin.
 *
 * Built on a real `<button role="switch">`, so Space and Enter, focus and the
 * disabled state are the platform's own, not reimplemented. The label is a
 * `<label for>` the button and fills the row's height, so a tap anywhere on
 * the words flips the switch; the button itself keeps the 44px floor on a
 * phone besides. The name is the label alone (`aria-labelledby`) and the hint
 * is the description, so a screen reader does not read the hint twice.
 *
 * Off is an outlined track with a small thumb in `faint`; on is the accent
 * fill with the thumb grown and slid across. The state is carried by position
 * and shape as well as colour, never colour alone. The thumb moves by
 * translate and scale only, on the state change and at no other time (the
 * reduced-motion rule in index.css collapses it to an instant flip).
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    onCheckedChange,
    label,
    hint,
    hideLabel = false,
    containerClassName,
    className,
    disabled,
    id: givenId,
    onClick,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? `switch-${autoId}`;
  const labelId = label ? `${id}-label` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;

  const control = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={hintId}
      disabled={disabled}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange(!checked);
      }}
      className={clsx(
        // The 44px floor on a phone; the track sits centred inside it.
        'group/switch tap-safe relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full',
        // The focus ring is drawn on the track, as the Checkbox draws it on
        // its box: on a phone this button is the 44px tap square, and a ring
        // round that square is a circle round a pill (24 Sep 2026 review).
        'focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-55',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className={clsx(
          'flex h-6 w-10 items-center rounded-full border p-[3px]',
          'transition-colors duration-150 ease-[var(--ease)]',
          'group-focus-visible/switch:outline-2 group-focus-visible/switch:outline-offset-2 group-focus-visible/switch:outline-accent',
          // A disabled switch that is on keeps its position but not the
          // accent: the accent says "live", and a switch that cannot be
          // moved is not.
          checked ? (disabled ? 'border-faint bg-faint' : 'border-accent bg-accent') : 'border-faint bg-surface',
        )}
      >
        <span
          className={clsx(
            'h-4 w-4 rounded-full',
            // Tailwind 4 writes translate-* and scale-* to their own CSS
            // properties, not to `transform`, so those are what transition.
            'transition-[translate,scale,background-color] duration-150 ease-[var(--ease)]',
            checked ? clsx('translate-x-4', disabled ? 'bg-surface' : 'bg-accent-ink') : 'scale-75 bg-faint',
          )}
        />
      </span>
    </button>
  );

  if (!label) return control;

  return (
    <div className={clsx('flex min-w-0 items-center justify-between gap-3', containerClassName)}>
      <label
        htmlFor={id}
        className={clsx(
          'flex min-w-0 flex-1 flex-col justify-center self-stretch',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          hideLabel && 'sr-only',
        )}
      >
        <span id={labelId} className={clsx('block text-body', disabled ? 'text-muted' : 'text-ink')}>
          {label}
        </span>
        {hint ? (
          <span id={hintId} className="block text-caption text-muted">
            {hint}
          </span>
        ) : null}
      </label>
      {control}
    </div>
  );
});
